// SupplierDetails.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SupplierModal from '../../components/Modals/SupplierModal';
import AddInvoiceModal from '../../components/Modals/AddInvoiceModal';
import * as XLSX from 'xlsx';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  FileText,
  Download,
  SquareChevronLeft,
  CreditCard,
  Globe2,
  Users,
  X,
  CheckCircle
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';

const SupplierDetails = ({ lang }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(lang);

  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [associatedAccounts, setAssociatedAccounts] = useState([]);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
  const provider_url = process.env.REACT_APP_API_PROVIDER_URL;
  const invoice_url = process.env.REACT_APP_API_INVOICE_URL;

  // États pour la modal de validation de token
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [token, setToken] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [isValidationSuccess, setIsValidationSuccess] = useState(false);

  const [filters, setFilters] = useState({
    invoiceNumber: '',
    status: '',
    dateFrom: '2024-01-01',
    dateTo: '2026-12-31'
  });
  const fetchInvoices = async () => {
    try {
      // 1. Préparation du corps de la requête (filtres)
      const requestBody = {
        providerId: id,
        invoiceNumber: filters.invoiceNumber,
        status: filters.status,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      };

      // Fonction utilitaire pour transformer une facture (pour éviter la répétition)
      const transformInvoice = (invoice) => ({
        id: invoice.id,
        number: invoice.invoice_number,
        date: invoice.invoice_at,
        status: invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1),
        amount: invoice.amount.toFixed(2),
        vat: '0.00',
        total: invoice.amount.toFixed(2),
        currency: invoice.currency,
        year: invoice.invoice_at ? new Date(invoice.invoice_at).getFullYear().toString() : 'N/A',
        provider_name: invoice.provider_name,
        paid_at: invoice.paid_at
      });

      // 2. Charger la PREMIÈRE PAGE pour obtenir les métadonnées (last_page)
      const firstResponse = await api.post(`${invoice_url}/provider/list`, requestBody, {
        params: { page: 1 }
      });

      const firstResult = firstResponse.data.result;
      const lastPage = firstResult.last_page; // On récupère le nombre total de pages

      // On initialise notre tableau de résultats avec les données de la page 1
      let allInvoices = firstResult.data.map(transformInvoice);

      // 3. Charger les AUTRES PAGES en parallèle (si elles existent)
      if (lastPage > 1) {
        const promises = [];

        for (let page = 2; page <= lastPage; page++) {
          promises.push(
            api.post(`${invoice_url}/provider/list`, requestBody, {
              params: { page: page }
            }).then(res => res.data.result.data.map(transformInvoice))
          );
        }

        // Attendre que toutes les requêtes se terminent
        const pagesResults = await Promise.all(promises);

        // Fusionner les résultats de chaque page dans le tableau principal
        pagesResults.forEach(pageData => {
          allInvoices.push(...pageData);
        });
      }

      // 4. Mettre à jour l'état final
      setInvoices(allInvoices);

    } catch (error) {
      console.error('Erreur lors du chargement des factures:', error);
      toast.error(t('errorLoadingInvoices'));
      setInvoices([]);
    }
  };

  // Fetch supplier details from API
  const fetchSupplierDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${provider_url}/${id}`);
      const data = response.data.result;

      // Transform API data to match our structure
      const transformedSupplier = {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        postal_code: data.postal_code,
        city: data.city?.name,
        country: data.country?.name,
        created_at: data.created_at,
        website: data.website || 'N/A',
        status: data.status,
        logo: data.logo,
        providerType: data.type?.name,
        currency: data.currency,
        chargeServiceAll: data.charge_service_all,
        chargeServiceTypeAll: data.charge_service_type_all,
        activatedAt: data.activated_at
      };

      setSupplier(transformedSupplier);
      const respo = await api.get(`${provider_url}/${id}/accounts`);
      const d = respo.data.result;

      // Transform contacts to associated accounts
      const transformedAccounts = d.map(contact => ({
        id: contact.id,
        name: contact.full_name,
        email: contact.email,
        phone: contact.phone,
        created_at: contact.created_at || 'N/A',
        status: contact.status
      })) || [];

      setAssociatedAccounts(transformedAccounts);

    } catch (error) {
      console.error('Erreur lors du chargement des détails fournisseur:', error);
      toast.error(t('errorLoadingSupplier'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchSupplierDetails();
      fetchInvoices();
    }
  }, [id]);

  const handleEdit = () => {
    setShowSupplierModal(true);
  };

  const toggleSupplierStatus = async () => {
    try {
      // Cas spécial : activation d'un fournisseur avec statut "new"
      if (supplier.status === 'new') {
        await handleNewSupplierActivation();
        return;
      }

      // Pour tous les autres statuts, utiliser l'ancienne API
      const newStatus = supplier.status === 'enabled' ? 'disabled' : 'enabled';

      await api.post(`/providers/update-status`, {
        dataIds: [id],
        status: newStatus
      });

      setSupplier(prev => ({ ...prev, status: newStatus }));
      toast.success(t('supplierStatusUpdated'));
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      toast.error(t('errorUpdatingStatus'));
    }
  };

  // Nouvelle fonction pour gérer l'activation d'un fournisseur "new"
  const handleNewSupplierActivation = async () => {
    try {
      const response = await api.get(`providers/${id}/activate`);

      if (response.data) {
        // Afficher simplement une modal d'information
        setShowTokenModal(true);
        setValidationMessage(t('activationEmailSent'));
        // setIsValidationSuccess(true);
      }
    } catch (error) {
      console.error('Erreur lors de l\'activation du fournisseur:', error);
      toast.error(t('errorActivatingSupplier'));
    }
  };

  // Validation du token d'activation
  // const handleTokenValidation = async () => {
  //   if (!token.trim()) {
  //     setValidationMessage(t('enterValidationCode'));
  //     return;
  //   }

  //   setIsValidatingToken(true);
  //   setValidationMessage('');

  //   try {
  //     const response = await api.get(`/validation/account/provider/${token}`);

  //     if (response.status === 200 || response.status === 201 || response.status === 202) {
  //       setIsValidationSuccess(true);
  //       setValidationMessage(t('supplierActivatedSuccess'));

  //       // Rafraîchir les données du fournisseur après activation réussie
  //       setTimeout(() => {
  //         setShowTokenModal(false);
  //         setToken('');
  //         setValidationMessage('');
  //         setIsValidationSuccess(false);
  //         fetchSupplierDetails(); // Recharger les données
  //       }, 2000);
  //     } else {
  //       setIsValidationSuccess(false);
  //       setValidationMessage(response.data.result?.message || t('invalidValidationCode'));
  //     }
  //   } catch (error) {
  //     console.error(t('errorValidatingToken'), error);
  //     setIsValidationSuccess(false);
  //     setValidationMessage(t('validationErrorTryAgain'));
  //   } finally {
  //     setIsValidatingToken(false);
  //   }
  // };

  // const handleCancelTokenValidation = () => {
  //   setShowTokenModal(false);
  //   setToken('');
  //   setValidationMessage('');
  //   setIsValidationSuccess(false);
  // };

  const getStatusButtonText = () => {
    if (!supplier) return t('activate');

    switch (supplier.status) {
      case 'enabled': return t('deactivate');
      case 'disabled': return t('reactivate');
      case 'new': return t('activate');
      default: return t('activate');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'enabled': return '#10b981';
      case 'disabled': return '#ef4444';
      case 'new': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const invoiceStats = {
    total: invoices.length,
    paid: invoices.filter(inv => inv.status.toLowerCase() === 'paid').length,
    pending: invoices.filter(inv => inv.status.toLowerCase() === 'unpaid').length,
    overdue: invoices.filter(inv => inv.status.toLowerCase() === 'overdue').length,

    totalAmount: invoices.reduce((sum, inv) => sum + Number(inv.amount), 0).toFixed(2),
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString(lang, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const exportToExcel = () => {
    if (!invoices || invoices.length === 0) {
      toast.error(t('noDataToExport'));
      return;
    }

    try {
      // Calculer les statistiques pour l'export
      const invoiceStats = {
        totalInvoices: invoices.length,
        paidInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'paid').length,
        unpaidInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'unpaid').length,
        overdueInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'overdue').length,
        totalAmount: invoices.reduce((sum, inv) => sum + parseFloat(inv.amount), 0).toFixed(2)
      };

      // Préparer les données pour Excel (dans l'ordre des colonnes du tableau)
      const excelData = invoices.map(invoice => ({
        [t('id')]: invoice.id,
        [t('invoiceNumber')]: invoice.number,
        [t('date')]: invoice.date,
        [t('status')]: invoice.status,
        [t('amount')]: invoice.amount,
        [t('paid_at')]: invoice.paid_at || t('notAvailable'),
        [t('currency')]: invoice.currency,
        [t('provider')]: invoice.provider_name || supplier?.name || 'N/A'
      }));

      // Créer une feuille de calcul
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Préparer les statistiques
      const statsData = [
        { [t('statistics')]: '' },
        { [t('statistics')]: t('totalInvoices'), '': invoiceStats.totalInvoices },
        { [t('statistics')]: t('paidInvoices'), '': invoiceStats.paidInvoices },
        { [t('statistics')]: t('unpaidInvoices'), '': invoiceStats.unpaidInvoices },
        { [t('statistics')]: t('overdueInvoices'), '': invoiceStats.overdueInvoices },
        { [t('statistics')]: t('totalAmount'), '': `${invoiceStats.totalAmount} ${invoices[0]?.currency || 'EUR'}` },
        { [t('statistics')]: '' },
        { [t('statistics')]: t('supplierInformation'), '': '' },
        { [t('statistics')]: t('name'), '': supplier?.name || 'N/A' },
        { [t('statistics')]: t('email'), '': supplier?.email || 'N/A' },
        { [t('statistics')]: t('phone'), '': supplier?.phone || 'N/A' },
        { [t('statistics')]: t('providerType'), '': supplier?.providerType || 'N/A' },
        { [t('statistics')]: '' },
        { [t('statistics')]: t('exportInformation'), '': '' },
        { [t('statistics')]: t('exportDate'), '': new Date().toLocaleString(lang) },
        { [t('statistics')]: t('filtersApplied'), '': '' },
        { [t('statistics')]: t('dateFrom'), '': filters.dateFrom || t('none') },
        { [t('statistics')]: t('dateTo'), '': filters.dateTo || t('none') },
        { [t('statistics')]: t('statusFilter'), '': filters.status ? t(filters.status) : t('allStatuses') },
        { [t('statistics')]: t('invoiceNumberFilter'), '': filters.invoiceNumber || t('none') },
      ];

      const wsStats = XLSX.utils.json_to_sheet(statsData, { skipHeader: true });

      // Créer un nouveau classeur avec plusieurs feuilles
      const wb = XLSX.utils.book_new();

      // Ajouter la feuille des données des factures
      XLSX.utils.book_append_sheet(wb, ws, t('invoices'));

      // Ajouter la feuille des statistiques
      XLSX.utils.book_append_sheet(wb, wsStats, t('statistics'));

      // Mettre en forme les colonnes (largeur automatique)
      if (ws['!ref']) {
        const range = XLSX.utils.decode_range(ws['!ref']);
        const wscols = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          let maxLength = 0;
          for (let R = range.s.r; R <= range.e.r; ++R) {
            const cell = ws[XLSX.utils.encode_cell({ c: C, r: R })];
            if (cell && cell.v) {
              const cellLength = cell.v.toString().length;
              if (cellLength > maxLength) maxLength = cellLength;
            }
          }
          wscols.push({ wch: Math.min(maxLength + 2, 40) }); // Max 40 caractères
        }
        ws['!cols'] = wscols;
      }

      // Mettre en forme les statistiques
      if (wsStats['!ref']) {
        const range = XLSX.utils.decode_range(wsStats['!ref']);
        const wscolsStats = [
          { wch: 30 }, // Première colonne (labels)
          { wch: 25 }  // Deuxième colonne (valeurs)
        ];
        wsStats['!cols'] = wscolsStats;
      }

      // Générer le nom du fichier avec la date et le nom du fournisseur
      const date = new Date();
      const dateStr = date.toISOString().split('T')[0];
      const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
      const supplierName = supplier?.name?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'supplier';
      const fileName = `invoices_${supplierName}_${dateStr}_${timeStr}.xlsx`;

      // Télécharger le fichier
      XLSX.writeFile(wb, fileName);

      toast.success(t('exportSuccess'));

    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      toast.error(t('exportError'));
    }
  };


  // Styles pour la modal de token
  const tokenModalStyles = {
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    modalContent: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      width: '400px',
      maxWidth: '90%',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      borderRadius: '6px',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    validationMessage: {
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px',
      fontWeight: '500'
    },
    successMessage: {
      background: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0'
    },
    errorMessage: {
      background: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #fecaca'
    },
    infoMessage: {
      background: '#e0f2fe',
      color: '#0c4a6e',
      border: '1px solid #bae6fd'
    },
    tokenInput: {
      width: '100%',
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      boxSizing: 'border-box',
      transition: 'all 0.2s ease'
    },
    tokenInputFocus: {
      borderColor: '#7c3aed',
      boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
    },
    modalButtons: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '20px'
    },
    secondaryButton: {
      padding: '10px 20px',
      background: 'transparent',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    tokenButton: {
      padding: '10px 20px',
      background: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease'
    },
    tokenButtonDisabled: {
      opacity: '0.6',
      cursor: 'not-allowed'
    }
  };

  const styles = {
    container: {
      padding: '8px 24px',
      background: '#f9fafb',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '8px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      backgroundColor: 'transparent',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
      marginBottom: '4px',
      '&:hover': {
        backgroundColor: '#f3f4f6',
        borderColor: '#9ca3af'
      }
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '400px'
    },
    loadingText: {
      fontSize: '18px',
      color: '#6b7280'
    },
    section: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: '1px solid #e5e7eb'
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    statusRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      padding: '16px',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    },
    status: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '16px',
      fontWeight: '600'
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '24px'
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '16px',
      background: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#f3f4f6',
        transform: 'translateY(-2px)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }
    },
    infoItemHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '4px'
    },
    infoIcon: {
      color: '#7c3aed'
    },
    infoLabel: {
      fontSize: '12px',
      color: '#6b7280',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    infoValue: {
      fontSize: '16px',
      color: '#1f2937',
      fontWeight: '500',
      lineHeight: '1.5'
    },
    infoSubValue: {
      fontSize: '14px',
      color: '#4b5563',
      fontWeight: '400'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center'
    },
    primaryButton: {
      padding: '10px 20px',
      background: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#6d28d9',
        transform: 'translateY(-1px)'
      }
    },
    secondaryButton: {
      padding: '10px 20px',
      background: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#0da271',
        transform: 'translateY(-1px)'
      }
    },
    statusButton: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      background: (supplier?.status === 'enabled') ? '#ef4444' :
        (supplier?.status === 'disabled') ? '#10b981' :
          (supplier?.status === 'new') ? '#f59e0b' : '#3b82f6',
      color: 'white',
      '&:hover': {
        opacity: '0.9',
        transform: 'translateY(-1px)'
      }
    },
    invoiceSummary: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: '16px',
      marginBottom: '24px',
      padding: '15px',
      background: '#667eea',
      borderRadius: '12px',
      color: 'white'
    },
    summaryItem: {
      textAlign: 'center',
      padding: '4px',
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      backdropFilter: 'blur(10px)'
    },
    summaryValue: {
      fontSize: '28px',
      fontWeight: 'bold',
      marginBottom: '4px',
      color: 'white'
    },
    summaryLabel: {
      fontSize: '14px',
      opacity: '0.9',
      color: 'white'
    },
    noData: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#6b7280',
      background: '#f9fafb',
      borderRadius: '8px',
      border: '2px dashed #d1d5db'
    },
    simpleTableContainer: {
      overflowX: 'auto',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    simpleTable: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: '0',
      background: 'white',
      textAlign: 'center',
      verticalAlign: 'middle'
    },
    tableHeader: {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',

    },
    tableHeaderCell: {
      padding: '16px',
      textAlign: 'center',
      color: 'white',
      fontWeight: '700',
      fontSize: '16px',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background 0.2s ease',
      '&:hover': {
        background: '#f9fafb'
      }
    },
    tableCell: {
      padding: '16px',
      fontSize: '14px',
      color: '#374151',
      borderBottom: '1px solid #e5e7eb'
    },
    statusBadge: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      color: 'white',
      textTransform: 'uppercase',
      display: 'inline-block',
      minWidth: '80px',
      textAlign: 'center'
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
    },
    editButton: {
      padding: '8px 16px',
      background: '#667eea',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s ease',
    },
    deleteButton: {
      padding: '8px 16px',
      background: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s ease',
    },
    totalRow: {
      background: '#f8fafc',
      borderTop: '2px solid #e5e7eb',
      fontWeight: '600'
    },
    totalCell: {
      padding: '16px',
      color: '#1f2937',
      fontSize: '15px'
    },
    contactBadge: {
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: '600',
      color: 'white',
      background: '#8b5cf6',
      marginLeft: '8px'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <button
          onClick={() => navigate('/suppliers')}
          style={styles.backButton}
        >
          <SquareChevronLeft size={20} />
          {t('backToSuppliers')}
        </button>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingText}>{t('loading')}...</div>
        </div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div style={styles.container}>
        <button
          onClick={() => navigate('/suppliers')}
          style={styles.backButton}
        >
          <SquareChevronLeft size={20} />
          {t('backToSuppliers')}
        </button>
        <div style={styles.noData}>
          <p>{t('supplierNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header avec bouton retour */}
      <div style={styles.header}>
        <button
          onClick={() => navigate('/suppliers')}
          style={styles.backButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
            e.currentTarget.style.borderColor = '#9ca3af';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#d1d5db';
          }}
        >
          <SquareChevronLeft size={20} />
          {t('backToSuppliers')}
        </button>
      </div>

      {/* Section Informations du fournisseur */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>
            <Building2 size={24} color="#7c3aed" />
            {t('supplierInformation')}
          </h2>
        </div>

        {/* Ligne État + Bouton */}
        <div style={styles.statusRow}>
          <div style={styles.status}>
            {t('status')}:
            <span style={{
              color: getStatusColor(supplier.status),
              textTransform: 'capitalize'
            }} title={supplier.status}>
              {supplier.status}
            </span>
            {supplier.activatedAt && (
              <span style={styles.infoSubValue}>
                • {t('activatedOn')}: {formatDate(supplier.activatedAt)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              style={styles.statusButton}
              onClick={toggleSupplierStatus}
            >
              {getStatusButtonText()}
            </button>
            <button
              style={styles.primaryButton}
              onClick={handleEdit}
            >
              {t('edit')}
            </button>
          </div>
        </div>

        {/* Grille d'informations améliorée */}
        <div style={styles.infoGrid}>
          <div style={styles.infoItem}>
            <div style={styles.infoItemHeader}>
              <Building2 size={18} style={styles.infoIcon} />
              <span style={styles.infoLabel}>{t('name')}</span>
            </div>
            <span style={styles.infoValue} title={supplier.name}>{supplier.name}</span>
            {supplier.providerType && (
              <span style={styles.infoSubValue}>
                {t('providerType')}: {supplier.providerType}
              </span>
            )}
          </div>

          <div style={styles.infoItem}>
            <div style={styles.infoItemHeader}>
              <Mail size={18} style={styles.infoIcon} />
              <span style={styles.infoLabel}>{t('email')}</span>
            </div>
            <span style={styles.infoValue} title={supplier.email}>{supplier.email}</span>
          </div>

          <div style={styles.infoItem}>
            <div style={styles.infoItemHeader}>
              <Phone size={18} style={styles.infoIcon} />
              <span style={styles.infoLabel}>{t('phone')}</span>
            </div>
            <span style={styles.infoValue} title={supplier.phone}>{supplier.phone}</span>
          </div>

          <div style={styles.infoItem}>
            <div style={styles.infoItemHeader}>
              <MapPin size={18} style={styles.infoIcon} />
              <span style={styles.infoLabel}>{t('address')}</span>
            </div>
            <span style={styles.infoValue} title={supplier.address}>{supplier.address}</span>
            <span style={styles.infoSubValue} title={`${supplier.postal_code} ${supplier.city}, ${supplier.country}`}>
              {supplier.postal_code} {supplier.city}, {supplier.country}
            </span>
          </div>

          <div style={styles.infoItem}>
            <div style={styles.infoItemHeader}>
              <Globe2 size={18} style={styles.infoIcon} />
              <span style={styles.infoLabel}>{t('website')}</span>
            </div>
            <span style={styles.infoValue} title={supplier.website}>
              <a
                href={`https://${supplier.website}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#3b82f6', textDecoration: 'none' }}
                title={supplier.website}
              >
                {supplier.website}
              </a>
            </span>
          </div>

          <div style={styles.infoItem}>
            <div style={styles.infoItemHeader}>
              <Calendar size={18} style={styles.infoIcon} />
              <span style={styles.infoLabel}>{t('creationDate')}</span>
            </div>
            <span style={styles.infoValue} title={supplier.created_at}>{supplier.created_at}</span>
          </div>

          <div style={styles.infoItem}>
            <div style={styles.infoItemHeader}>
              <CreditCard size={18} style={styles.infoIcon} />
              <span style={styles.infoLabel}>{t('currency')}</span>
            </div>
            <span style={styles.infoValue} title={supplier.currency}>{supplier.currency}</span>
          </div>

          {/* <div style={styles.infoItem}>
            <div style={styles.infoItemHeader}>
              <Tag size={18} style={styles.infoIcon} />
              <span style={styles.infoLabel}>{t('charges')}</span>
            </div>
            <span style={styles.infoValue} title={`${supplier.chargeServiceAll}% (${supplier.chargeServiceTypeAll})`}>
              {supplier.chargeServiceAll}% ({supplier.chargeServiceTypeAll})
            </span>
          </div> */}

        </div>
      </div>

      {/* Section Comptes associés */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>
            <Users size={24} color="#7c3aed" />
            {t('associatedAccounts')}
            <span style={{
              fontSize: '14px',
              color: '#6b7280',
              marginLeft: '8px'
            }}>
              ({associatedAccounts.length})
            </span>
          </h2>
        </div>

        {associatedAccounts.length > 0 ? (
          <div style={styles.simpleTableContainer}>
            <table style={styles.simpleTable}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.tableHeaderCell}>{t('id')}</th>
                  <th style={styles.tableHeaderCell}>{t('name')}</th>
                  <th style={styles.tableHeaderCell}>{t('email')}</th>
                  <th style={styles.tableHeaderCell}>{t('phone')}</th>
                  <th style={styles.tableHeaderCell}>{t('created_at')}</th>
                  <th style={styles.tableHeaderCell}>{t('status')}</th>
                </tr>
              </thead>
              <tbody>
                {associatedAccounts.map((account) => (
                  <tr
                    key={account.id}
                    style={{
                      ...styles.tableRow,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onClick={() => navigate(`/users/${account.id}`)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d9f5d7ff'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={styles.tableCell} title={account.name}>{account.id}</td>
                    <td style={styles.tableCell} title={account.name}>
                      {account.name}
                      {account.isPrimary && (
                        <span style={styles.contactBadge} title={t('primary')}>
                          {t('primary')}
                        </span>
                      )}
                    </td>
                    <td style={styles.tableCell} title={account.email}>{account.email}</td>
                    <td style={styles.tableCell} title={account.phone}>{account.phone}</td>
                    <td style={styles.tableCell} title={account.created_at}>{account.created_at}</td>
                    <td style={styles.tableCell} title={account.status}>
                      <span style={{
                        ...styles.statusBadge,
                        background: account.status === 'enabled' ? '#10b981' : '#6b7280'
                      }}>
                        {account.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.noData}>
            <Users size={48} color="#9ca3af" />
            <p style={{ marginTop: '16px' }}>{t('noAssociatedAccounts')}</p>
          </div>
        )}
      </div>

      {/* Section Factures (conservée) */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>
            <FileText size={24} color="#7c3aed" />
            {t('invoices')}
            <span style={{
              fontSize: '14px',
              color: '#6b7280',
              marginLeft: '8px'
            }}>
              ({invoices.length})
            </span>
          </h2>

          {invoices.length > 0 ? (
            <div style={styles.buttonGroup}>
              <button
                style={styles.secondaryButton}
                onClick={exportToExcel}
              >
                <Download size={16} />
                {t('export')}
              </button>
              {/* <button
                style={styles.primaryButton}
                onClick={() => setShowAddInvoiceModal(true)}
              >
                <Plus size={16} />
                {t('addInvoice')}
              </button> */}
            </div>
          ) : (
            null
          )}
        </div>

        {/* Résumé des factures amélioré */}
        {invoices.length > 0 && (
          <div style={styles.invoiceSummary}>
            <div style={styles.summaryItem}>
              <div style={styles.summaryValue}>{invoiceStats.total}</div>
              <div style={styles.summaryLabel}>{t('totalInvoices')}</div>
            </div>
            <div style={styles.summaryItem}>
              <div style={styles.summaryValue}>{invoiceStats.paid}</div>
              <div style={styles.summaryLabel}>{t('paidInvoices')}</div>
            </div>
            <div style={styles.summaryItem}>
              <div style={styles.summaryValue}>{invoiceStats.pending}</div>
              <div style={styles.summaryLabel}>{t('pendingInvoices')}</div>
            </div>
            <div style={styles.summaryItem}>
              <div style={styles.summaryValue}>{invoiceStats.overdue}</div>
              <div style={styles.summaryLabel}>{t('overdueInvoices')}</div>
            </div>
          </div>
        )}

        {invoices.length > 0 ? (
          <div style={styles.simpleTableContainer}>
            <table style={styles.simpleTable}>
              <thead>
                <tr style={{ ...styles.tableHeader, background: '#667eea' }}>
                  <th style={styles.tableHeaderCell}>{t('id')}</th>
                  <th style={styles.tableHeaderCell}>{t('invoiceNumber')}</th>
                  <th style={styles.tableHeaderCell}>{t('date')}</th>
                  <th style={styles.tableHeaderCell}>{t('status')}</th>
                  <th style={styles.tableHeaderCell}>{t('amount')}</th>
                  <th style={styles.tableHeaderCell}>{t('paid_at')}</th>
                  <th style={styles.tableHeaderCell}>{t('currency')}</th>
                  <th style={styles.tableHeaderCell}>{t('provider')}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}
                    style={{
                      ...styles.tableRow,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onClick={() => navigate(`/invoices/suppliers/${invoice.id}`)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#667eea22'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={styles.tableCell} title={invoice.id}>{invoice.id}</td>
                    <td style={styles.tableCell} title={invoice.number}>
                      <strong>{invoice.number}</strong>
                    </td>
                    <td style={styles.tableCell} title={invoice.date}>{invoice.date}</td>
                    <td style={styles.tableCell} title={invoice.status}>
                      <span style={{
                        ...styles.statusBadge,
                        background: invoice.status === 'Paid' ? '#10b981' :
                          invoice.status === 'Unpaid' ? '#f59e0b' : '#ef4444'
                      }}>
                        {invoice.status}
                      </span>
                    </td>
                    <td style={styles.tableCell} title={invoice.amount}>{invoice.amount}</td>
                    <td style={styles.tableCell} title={invoice.paid_at}>
                      <strong>{invoice.paid_at || t('notAvailable')}</strong>
                    </td>
                    <td style={styles.tableCell} title={invoice.currency}>{invoice.currency}</td>
                    <td style={styles.tableCell} title={invoice.provider_name}>{invoice.provider_name}</td>

                  </tr>
                ))}
                {/* Ligne des totaux */}
                <tr style={styles.totalRow}>
                  <td style={styles.totalCell} colSpan="4">
                    <strong>{t('totals')}:</strong>
                  </td>
                  <td style={styles.totalCell}>
                    <strong>{invoiceStats.totalAmount} </strong>
                  </td>
                  <td style={styles.totalCell} colSpan="3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.noData}>
            <FileText size={48} color="#9ca3af" />
            <p style={{ marginTop: '16px' }}>{t('noInvoices')}</p>
            {/* <button style={{
              ...styles.secondaryButton,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
              onClick={() => setShowAddInvoiceModal(true)}>
              <Plus size={16} />
              {t('createFirstInvoice')}
            </button> */}
          </div>
        )}
      </div>

      {/* Modal d'édition du fournisseur */}
      {showSupplierModal && (
        <SupplierModal
          supplier={supplier}
          type={'edit'}
          onClose={() => setShowSupplierModal(false)}
          onSave={() => {
            fetchSupplierDetails();
            toast.success(t('supplierUpdatedSuccess'));
            setShowSupplierModal(false);
          }}
          lang={lang}
        />
      )}

      {/* Modal de validation du token d'activation */}
      {showTokenModal && (
        <div style={tokenModalStyles.modalOverlay}>
          <div style={tokenModalStyles.modalContent}>
            <div style={tokenModalStyles.modalHeader}>
              <h2 style={tokenModalStyles.modalTitle}>
                <CheckCircle size={24} color="#10b981" />
                {t('activationInitiated')}
              </h2>
              <button
                style={tokenModalStyles.closeButton}
                onClick={() => setShowTokenModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '12px', color: '#374151', lineHeight: '1.6' }}>
                {validationMessage}
              </p>
              <p style={{ color: '#6b7280', fontSize: '14px' }}>
                {t('activationLinkSentToSupplier')}
              </p>
            </div>

            <div style={tokenModalStyles.modalButtons}>
              <button
                style={tokenModalStyles.tokenButton}
                onClick={() => setShowTokenModal(false)}
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* {showAddInvoiceModal && (
        <AddInvoiceModal
          supplierId={id}
          supplierName={supplier?.name}
          onClose={() => setShowAddInvoiceModal(false)}
          onSuccess={(newInvoice) => {
            // Rafraîchir la liste des factures
            fetchInvoices();
            toast.success(t('invoiceCreatedSuccess'));
            setShowAddInvoiceModal(false);
          }}
          lang={lang}
        />
      )} */}

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default SupplierDetails;